import { Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ShowDeviceFacade } from '../../../../../Facades/Intune/show-device.facade';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { Device } from '../../../../../Models/avyna/device.model';
import { Storage } from '../../../../../Models/avyna/agent-device.model';
import { AvCardComponent } from '../../../../Shared/Components/av-card/av-card.component';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvTableCard } from '../../../../Shared/Components/av-table-card/av-table-card.component';
import { AvTableColumn, AvTableProgressCell, AvTableRow } from '../../../../Shared/Interfaces/av-table-card.interface';

@Component({
  selector: 'app-hardware',
  standalone: true,
  imports: [COMMON_IMPORTS, AvCardComponent, AvContentContainerComponent, AvTableCard],
  templateUrl: './hardware.component.html',
  styleUrls: ['./hardware.component.scss'],
})

export class HardwareComponent {
  private readonly _facade = inject(ShowDeviceFacade);
  private readonly translate = inject(TranslateService);

  readonly device = this._facade.device;

  readonly bios = computed(() => {
    const d = this.device();
    const biosData = d?.agent?.bios;
    return {
      manufacturer: biosData?.manufacturer || d?.intune?.manufacturer || 'Unknown',
      version: biosData?.version || d?.intune?.hardwareInformation?.systemManagementBIOSVersion || this.translate.instant('shared.general.labels.na'),
      assetTag: biosData?.asset_tag ?? this.translate.instant('shared.general.labels.na'),
      ownershipTag: biosData?.ownership_tag ?? this.translate.instant('shared.general.labels.na'),
      setupPassword: biosData?.is_setup_password_set,
      wakeOnLan: biosData?.wake_on_lan_enabled,
    };
  });

  readonly biosFields = computed(() => {
    const b = this.bios();
    return [
      { label: 'app.device.hardware.sections.bios.fields.manufacturer', value: b.manufacturer },
      { label: 'app.device.hardware.sections.bios.fields.version', value: b.version },
      { label: 'app.device.hardware.sections.bios.fields.asset_tag', value: b.assetTag },
      { label: 'app.device.hardware.sections.bios.fields.ownership_tag', value: b.ownershipTag },
      { label: 'app.device.hardware.sections.bios.fields.setup_password', value: b.setupPassword },
      { label: 'app.device.hardware.sections.bios.fields.wake_on_lan', value: b.wakeOnLan },
    ];
  });

  readonly processor = computed(() => {
    const d = this.device();
    const agentProc = d?.agent?.processor;
    const intune = d?.intune;
    return {
      name: agentProc?.cpu_name ?? this.translate.instant('shared.general.labels.na'),
      manufacturer: agentProc?.cpu_manufacturer ?? intune?.manufacturer ?? intune?.hardwareInformation?.manufacturer ?? this.translate.instant('shared.general.labels.na'),
      speed: agentProc?.cpu_speed_mhz != null ? `${agentProc.cpu_speed_mhz} MHz` : this.translate.instant('shared.general.labels.na'),
      physicalProcessors: agentProc?.cpu_physical_processors != null ? String(agentProc.cpu_physical_processors) : this.translate.instant('shared.general.labels.na'),
      cores: agentProc?.cpu_cores != null ? String(agentProc.cpu_cores) : this.translate.instant('shared.general.labels.na'),
      logicalProcessors: agentProc?.cpu_logical_processors != null ? String(agentProc.cpu_logical_processors) : this.translate.instant('shared.general.labels.na'),
    };
  });

  readonly processorFields = computed(() => {
    const p = this.processor();
    return [
      { label: 'app.device.hardware.sections.processor.fields.name', value: p.name },
      { label: 'app.device.hardware.sections.processor.fields.manufacturer', value: p.manufacturer },
      { label: 'app.device.hardware.sections.processor.fields.speed', value: p.speed },
      { label: 'app.device.hardware.sections.processor.fields.physical_processors', value: p.physicalProcessors },
      { label: 'app.device.hardware.sections.processor.fields.cores', value: p.cores },
      { label: 'app.device.hardware.sections.processor.fields.logical_processors', value: p.logicalProcessors },
    ];
  });

  readonly storageColumns: AvTableColumn[] = [
    { key: 'caption', label: 'app.device.hardware.storage.columns.caption', sortable: true },
    { key: 'type', label: 'app.device.hardware.storage.columns.type', sortable: true },
    { key: 'fileSystem', label: 'app.device.hardware.storage.columns.file_system', sortable: true },
    { key: 'status', label: 'app.device.hardware.storage.columns.status', sortable: true },
    { key: 'encrypted', label: 'app.device.hardware.storage.columns.encrypted', type: 'bool', sortable: true },
    { key: 'mounted', label: 'app.device.hardware.storage.columns.mounted', type: 'bool', sortable: true },
    { key: 'usage', label: 'app.device.hardware.storage.columns.used', type: 'progress', sortable: true },
  ];

  private readonly storageRowsInternal = computed(() => {
    const device = this.device();
    const rows = this.buildRowsFromAgentStorage(device);
    if (rows.length > 0) return rows;
    return this.buildFallbackRow(device);
  });

  readonly storageRows = computed(() => {
    return this.storageRowsInternal().map((row) => ({
      caption: row.caption,
      type: row.type,
      fileSystem: row.fileSystem,
      status: row.status,
      encrypted: row.encrypted,
      mounted: row.mounted,
      usage: this.buildUsageCell(row),
    }));
  });

  private buildUsageCell(row: { usedPercent: number | null; usedGb: number | null; sizeGb: number | null }): AvTableProgressCell {
    if (row.usedPercent === null || row.usedPercent === undefined) {
      return { text: this.translate.instant('shared.general.labels.na'), percent: null };
    }
    const usedGb = row.usedGb;
    const sizeGb = row.sizeGb;
    const gbLabel = this.translate.instant('shared.general.labels.gb');
    const text = usedGb !== null && sizeGb !== null
      ? `${usedGb}/${sizeGb} ${gbLabel}`
      : this.translate.instant('shared.general.labels.na');
    return { text, percent: row.usedPercent };
  }

  private buildRowsFromAgentStorage(device: Device | null) {
    const agentItems = this.getAgentStorageItems(device);
    return agentItems.map((item) => this.mapAgentItemToRow(item, device));
  }

  private getAgentStorageItems(device: Device | null): Storage[] {
    const direct = Array.isArray(device?.agent?.storage) ? (device?.agent?.storage as Storage[]) : [];
    const storageWithItems = device?.agent?.storage as { items?: Storage[] } | undefined;
    const items = Array.isArray(storageWithItems?.items) ? storageWithItems.items ?? [] : [];
    return direct.length ? direct : items;
  }

  private mapAgentItemToRow(item: Storage, device: Device | null) {
    const sizeGb = item.size_gb ?? null;
    const remainingGb = item.storage_remaining_gb ?? null;
    const usedGb = sizeGb !== null && remainingGb !== null ? Math.max(sizeGb - remainingGb, 0) : null;
    const usedPercent =
      item.storage_used_percent !== null && item.storage_used_percent !== undefined
        ? Math.round(item.storage_used_percent * 100) / 100
        : null;

    return {
      caption: item.caption || this.translate.instant('shared.general.labels.na'),
      type: item.type || device?.intune?.chassisType || device?.intune?.deviceType || 'Unknown',
      sizeGb,
      usedPercent,
      usedGb,
      remainingGb,
      fileSystem: item.file_system || this.translate.instant('shared.general.labels.na'),
      status: item.status || device?.intune?.managementState || 'Unknown',
      encrypted: item.encrypted ?? null,
      mounted: item.mounted ?? null,
    };
  }

  private buildFallbackRow(device: Device | null) {
    const totalBytes = device?.intune?.totalStorageSpaceInBytes ?? device?.intune?.hardwareInformation?.totalStorageSpace ?? null;
    const freeBytes = device?.intune?.freeStorageSpaceInBytes ?? device?.intune?.hardwareInformation?.freeStorageSpace ?? null;
    if (totalBytes === null) return [];

    const sizeGb = totalBytes ? Math.round(totalBytes / (1024 ** 3)) : null;
    const freeGb = freeBytes !== null ? Math.round(freeBytes / (1024 ** 3)) : null;
    const usedGb = sizeGb !== null && freeGb !== null ? Math.max(sizeGb - freeGb, 0) : null;
    const usedPercent = sizeGb && usedGb !== null && sizeGb > 0 ? Math.round((usedGb / sizeGb) * 100) : null;

    return [{
      caption: device?.intune?.displayDeviceName() || this.translate.instant('shared.general.labels.na'),
      type: device?.intune?.chassisType || device?.intune?.deviceType || 'Unknown',
      sizeGb,
      usedPercent,
      usedGb,
      remainingGb: freeGb,
      fileSystem: this.translate.instant('shared.general.labels.na'),
      status: device?.intune?.managementState || 'Unknown',
      encrypted: device?.intune?.isEncrypted ?? null,
      mounted: null,
    }];
  }
}
